import * as React from "react";
import type { ComponentType } from "react";

export type AnyContext = unknown;

export const nullElement: null = null;

export const emptyRef = null;

export const noStateChange = null;

export function isChildStringOrNumber(x: React.ReactNode): x is string | number {
    return typeof x === "string" || typeof x === "number";
}

export function isChildFragment(x: React.ReactNode): x is React.ReactFragment {
    return Array.isArray(x);
}

export function isChildHtmlElement<T extends keyof JSX.IntrinsicElements>(
    x: React.ReactNode,
    type: T
): x is React.ReactElement<JSX.IntrinsicElements[T]> {
    return React.isValidElement(x) && x.type === type;
}

export function internalAreComponentsEqual<T>(
    typeA: string | React.ComponentType<T>,
    typeB: string | React.ComponentType<T>
): boolean {
    if (typeof typeA === "string" || typeof typeB === "string") {
        return typeA === typeB;
    }

    const actualTypeA = getActualCompType(typeA);
    const actualTypeB = getActualCompType(typeB);

    // Fast Refresh preserves component identity, so simple reference equality is sufficient
    return actualTypeA === actualTypeB;
}

/** Extract the actual component type from wrappers (memo, forwardRef) */
const getActualCompType = <T>(comp: ComponentType<T>) => {
    if (comp && typeof comp === "object" && "$$typeof" in comp) {
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return comp.type || comp;
    }
    return comp;
};

export function isChildOfType<P>(x: React.ReactNode, type: React.ComponentType<P>): x is React.ReactElement<P> {
    return React.isValidElement(x) && internalAreComponentsEqual(x.type, type);
}

export function ofReactType<P>(type: React.ComponentType<P>): (x: React.ReactNode) => x is React.ReactElement<P> {
    return (x: React.ReactNode): x is React.ReactElement<P> => isChildOfType(x, type);
}

export type ExtractProps<TComp> = TComp extends React.ComponentType<infer TProps> ? TProps : "CannotExtractPropsFrom";

export function pullContext<TContext>(component: React.Component): TContext {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return component.context;
}

export function extractNameFromComponent<T>(comp: string | React.ComponentType<T>): string | undefined {
    if (typeof comp === "string") {
        return comp;
    }
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return comp.displayName ?? comp.render?.name ?? comp.name;
}

export function createHocDisplayName<T>(comp: React.ComponentType<T>, hocName: string): string | undefined {
    return `with${hocName}(${extractNameFromComponent(comp)})`;
}

export type GetProps<C> = C extends React.ComponentType<infer P> ? P : never;
